// src/pages/AdminContactTypesPage.js
//
// Admin management for Address Book contact types (categories). These drive the
// "Type" dropdown in the Address Book lookup modal; admins add/rename/retire/
// delete them here so the list can evolve over time. Backed by the
// /address-book/categories endpoints (ADMIN only).
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import api from "../api";

const EMPTY_FORM = { name: "", sort_order: "", active: true };

export default function AdminContactTypesPage() {
  const [types, setTypes]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  const [modal, setModal]           = useState(null); // "add" | "edit" | "delete"
  const [target, setTarget]         = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [actionError, setActionError] = useState("");
  const [toggling, setToggling]     = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await api.get("/address-book/categories");
      setTypes(res.data.items || []);
    } catch {
      setError("Failed to load contact types.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function field(key) {
    return (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  }

  function openAdd() {
    setForm({ ...EMPTY_FORM, sort_order: String((types.length + 1) * 10) });
    setActionError("");
    setTarget(null);
    setModal("add");
  }

  function openEdit(t) {
    setForm({ name: t.name ?? "", sort_order: String(t.sort_order ?? 0), active: !!t.active });
    setActionError("");
    setTarget(t);
    setModal("edit");
  }

  function openDelete(t) {
    setActionError("");
    setTarget(t);
    setModal("delete");
  }

  function closeModal() {
    setModal(null);
    setActionError("");
  }

  async function handleAdd(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      setActionError("Name is required.");
      return;
    }
    setSaving(true);
    setActionError("");
    try {
      await api.post("/address-book/categories", {
        name: form.name.trim(),
        sort_order: Number(form.sort_order) || 0,
      });
      closeModal();
      await load();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to create type.");
    } finally {
      setSaving(false);
    }
  }

  async function handleEdit(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      setActionError("Name is required.");
      return;
    }
    setSaving(true);
    setActionError("");
    try {
      await api.put(`/address-book/categories/${target.id}`, {
        name: form.name.trim(),
        sort_order: Number(form.sort_order) || 0,
        active: !!form.active,
      });
      closeModal();
      await load();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to update type.");
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(t) {
    setToggling(t.id);
    try {
      await api.put(`/address-book/categories/${t.id}`, { active: !t.active });
      await load();
    } catch {
      setError("Failed to update type status.");
    } finally {
      setToggling(null);
    }
  }

  async function handleDelete() {
    setSaving(true);
    setActionError("");
    try {
      await api.delete(`/address-book/categories/${target.id}`);
      closeModal();
      await load();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to delete type.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="p-3">
        <Spinner animation="border" size="sm" className="me-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="d-flex align-items-center mb-4 gap-3">
        <Link to="/admin" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1" />Back
        </Link>
        <h3 className="mb-0">
          <i className="bi bi-tags-fill me-2" />
          Contact Types
        </h3>
        <Button size="sm" className="ms-auto" onClick={openAdd}>
          <i className="bi bi-plus-lg me-1" />Add Type
        </Button>
      </div>

      <p className="text-muted">
        These types appear in the Address Book lookup. Retire a type to hide it from
        new entries while keeping existing contacts intact; a type still in use can’t
        be deleted.
      </p>

      {error && <Alert variant="danger">{error}</Alert>}

      {types.length === 0 && !error ? (
        <p className="text-muted">No contact types have been added yet.</p>
      ) : (
        <Table hover responsive size="sm">
          <thead>
            <tr>
              <th style={{ width: 80 }}>Order</th>
              <th>Name</th>
              <th>Contacts</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {types.map((t) => {
              const isBusy = toggling === t.id;
              const inUse = (t.contact_count ?? 0) > 0;
              return (
                <tr key={t.id} className={!t.active ? "table-secondary" : ""}>
                  <td className="align-middle text-muted">{t.sort_order}</td>
                  <td className="align-middle fw-semibold">{t.name}</td>
                  <td className="align-middle">{t.contact_count ?? 0}</td>
                  <td className="align-middle">
                    {t.active
                      ? <Badge bg="success">Active</Badge>
                      : <Badge bg="secondary">Retired</Badge>}
                  </td>
                  <td className="align-middle">
                    <div className="d-flex gap-2">
                      <Button size="sm" variant="outline-primary" onClick={() => openEdit(t)}>
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant={t.active ? "outline-secondary" : "outline-success"}
                        disabled={isBusy}
                        onClick={() => handleToggleActive(t)}
                      >
                        {isBusy
                          ? <Spinner animation="border" size="sm" />
                          : t.active ? "Retire" : "Restore"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={inUse}
                        title={inUse ? "In use by contacts — retire instead" : "Delete"}
                        onClick={() => openDelete(t)}
                      >
                        <i className="bi bi-trash" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {/* Add Type Modal */}
      <Modal show={modal === "add"} onHide={closeModal} centered>
        <Form onSubmit={handleAdd}>
          <Modal.Header closeButton>
            <Modal.Title>Add Contact Type</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {actionError && <Alert variant="danger" className="py-2">{actionError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Name <span className="text-danger">*</span></Form.Label>
              <Form.Control value={form.name} onChange={field("name")} placeholder="e.g. Firewood, Baby Pantry" autoFocus />
            </Form.Group>
            <Form.Group>
              <Form.Label>Sort Order</Form.Label>
              <Form.Control type="number" value={form.sort_order} onChange={field("sort_order")} />
              <Form.Text className="text-muted">Lower numbers appear first in the Type list.</Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Create Type"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Type Modal */}
      <Modal show={modal === "edit"} onHide={closeModal} centered>
        <Form onSubmit={handleEdit}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Contact Type</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {actionError && <Alert variant="danger" className="py-2">{actionError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Name <span className="text-danger">*</span></Form.Label>
              <Form.Control value={form.name} onChange={field("name")} autoFocus />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Sort Order</Form.Label>
              <Form.Control type="number" value={form.sort_order} onChange={field("sort_order")} />
            </Form.Group>
            <Form.Check
              type="switch"
              id="ct-active"
              label="Active"
              checked={form.active}
              onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Type Modal */}
      <Modal show={modal === "delete"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Contact Type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionError && <Alert variant="danger" className="py-2">{actionError}</Alert>}
          <p className="mb-0">
            Delete the type <strong>{target?.name}</strong>? This can’t be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
